import './text.less';
import classNames from 'classnames';
import { cloneDeep, isNull, toNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Input, InputNumber, Select } from '@meitu/saas-ui';
import {
    ILayerProps,
    IMarkupLayerProps,
    MarkupLabelType,
    MarkupLabelUnit,
    MarkupType,
} from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { editor } from '@/instance';

const UnitList = () => {
    const locale = getLocaleStore().locale;
    if (locale === 'cn') {
        return [
            { value: MarkupLabelUnit.mm, text: 'mm（毫米）' },
            { value: MarkupLabelUnit.cm, text: 'cm（厘米）' },
            { value: MarkupLabelUnit.inch, text: 'inch（英寸）' },
            { value: MarkupLabelUnit.m, text: 'm（米）' },
        ];
    }
    return [
        { value: MarkupLabelUnit.mm, text: 'mm' },
        { value: MarkupLabelUnit.cm, text: 'cm' },
        { value: MarkupLabelUnit.inch, text: 'inch' },
        { value: MarkupLabelUnit.m, text: 'm' },
    ];
};

const data = [MarkupLabelUnit.mm, MarkupLabelUnit.cm, MarkupLabelUnit.inch, MarkupLabelUnit.m];

export const LabelText: React.FC<{
    id: string;
    onAfterChange?: Function;
    trackCode?: {
        type?: string | number;
        text?: string | number;
        num?: string | number;
        units?: string | number;
    };
}> = React.memo(({ id, onAfterChange, trackCode }) => {
    const { $t } = useLocaleStore();
    const [open, setOpen] = useState(false);
    const resUnits = useRef<Array<MarkupLabelUnit | null>>([]);
    const originValue = useRef<{
        id: string;
        type: MarkupLabelType;
        text: string;
        num: number;
        units: MarkupLabelUnit[];
    }>();
    const oldLayer = useRef<ILayerProps | null>(null);

    const res = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;
        const { label, markupType } = layer.data as IMarkupLayerProps;
        return {
            type: label.type,
            text: label.text,
            num: label.num,
            units: label.units,
            fontFamily: label.fontFamily,
            markupType,
        };
    });

    useEffect(() => {
        const units = res?.units;
        if (!units) return;
        for (let i = 0; i < data.length; i++) {
            resUnits.current[i] = units.includes(data[i]) ? data[i] : null;
        }
    }, [res?.units]);

    const globalWidgetRef = useGlobalWidget(editor, false);

    if (isNull(res)) return null;

    const { type, text, num, units, markupType, fontFamily } = res;

    const dropdownRender = () => {
        return (
            <>
                {UnitList().map((item, index) => {
                    return (
                        <div key={item.value} className="c-markup-label__unit-item">
                            <Checkbox
                                className="c-markup-label__unit-item-checkbox"
                                checked={units.includes(item.value)}
                                onChange={(e) => {
                                    resUnits.current[index] = e.target.checked ? data[index] : null;
                                    editor.markupLayer.setLabelType(
                                        id,
                                        type,
                                        text,
                                        num,
                                        [...(resUnits.current.filter((v) => v !== null) as MarkupLabelUnit[])],
                                        editor.stage.getLayer(id, true),
                                    );
                                    onAfterChange?.(trackCode?.units);
                                }}
                            >
                                {item.text}
                            </Checkbox>
                        </div>
                    );
                })}
                <div className="c-markup-label__unit-tip">{$t((v) => v.panel.markupPanel.unitTip)}</div>
            </>
        );
    };

    return (
        <div className="c-markup-panel__item">
            {/* 箭头标注 */}
            {markupType === MarkupType.Arrow ? (
                <>
                    <LabelAction title={$t((v) => v.panel.markupPanel.type)} align={true}>
                        <div className="c-markup-label__type">
                            <div
                                className={classNames('c-markup-label__type-item', {
                                    active: type === MarkupLabelType.num,
                                })}
                                onClick={() => {
                                    if (type === MarkupLabelType.num) return;
                                    editor.markupLayer.setLabelType(
                                        id,
                                        MarkupLabelType.num,
                                        text,
                                        num,
                                        units,
                                        editor.stage.getLayer(id, true),
                                    );
                                    onAfterChange?.(trackCode?.type);
                                }}
                            >
                                {$t((v) => v.panel.markupPanel.num)}
                            </div>
                            <div
                                className={classNames('c-markup-label__type-item', {
                                    active: type === MarkupLabelType.custom,
                                })}
                                onClick={() => {
                                    if (type === MarkupLabelType.custom) return;
                                    const resText = text || 'designkit';
                                    editor.markupLayer.setLabelType(
                                        id,
                                        MarkupLabelType.custom,
                                        resText,
                                        num,
                                        units,
                                        editor.stage.getLayer(id, true),
                                    );
                                    onAfterChange?.(trackCode?.type);
                                }}
                            >
                                {$t((v) => v.panel.markupPanel.custom)}
                            </div>
                        </div>
                    </LabelAction>

                    {type === MarkupLabelType.num ? (
                        <LabelAction title={$t((v) => v.panel.markupPanel.num)} align={true}>
                            <div className="c-markup-label__num">
                                <InputNumber
                                    value={num}
                                    min={0.01}
                                    precision={2}
                                    onChange={(e) => {
                                        editor.markupLayer.setLabelType(id, type, text, toNumber(e) || 1, units);
                                    }}
                                    onFocusCapture={() => {
                                        originValue.current = cloneDeep({ id, type, text, num, units });
                                        oldLayer.current = editor.stage.getLayer(id, true);
                                        editor.markupLayer.setAttrOperateOriginData(cloneDeep(oldLayer.current));
                                    }}
                                    onBlur={() => {
                                        if (
                                            oldLayer.current &&
                                            originValue.current &&
                                            originValue.current.num !== num
                                        ) {
                                            editor.markupLayer.setLabelType(
                                                id,
                                                type,
                                                text,
                                                num,
                                                units,
                                                oldLayer.current,
                                            );
                                            onAfterChange?.(trackCode?.num);
                                        }
                                        editor.markupLayer.clearAttrOperateOriginData();
                                    }}
                                />
                            </div>
                        </LabelAction>
                    ) : null}

                    {type === MarkupLabelType.num ? (
                        <LabelAction title={$t((v) => v.panel.markupPanel.unit)} align={true}>
                            <div className="c-markup-label__unit">
                                <Select
                                    className="c-markup-label__unit-selector"
                                    popupClassName={'c-markup-label__unit-dropdown'}
                                    open={open}
                                    onDropdownVisibleChange={(visible) => {
                                        setOpen(visible);
                                        globalWidgetRef.current = visible;
                                    }}
                                    suffixIcon={<IconFont fontSize={10} name="ChevronDownBlack" />}
                                    dropdownRender={dropdownRender}
                                ></Select>
                                <div className="c-markup-label__unit-value">
                                    {units.length ? units.join('/') : $t((v) => v.panel.markupPanel.unitPlaceholder)}
                                </div>
                            </div>
                        </LabelAction>
                    ) : null}
                </>
            ) : null}

            {/* 文案输入框 */}
            {type === MarkupLabelType.custom ? (
                <LabelAction title={$t((v) => v.panel.markupPanel.text)} align={true}>
                    <div className="c-markup-label__text">
                        <Input
                            value={text}
                            className={classNames('c-markup-label__text-input')}
                            placeholder={$t((v) => v.panel.markupPanel.textPlaceholder)}
                            onChange={(e) => {
                                const { value } = e.target;
                                editor.markupLayer.setLabelType(id, type, value.trim(), num, units);
                            }}
                            onFocusCapture={() => {
                                originValue.current = cloneDeep({ id, type, text, num, units });
                                oldLayer.current = editor.stage.getLayer(id, true);
                                editor.markupLayer.setAttrOperateOriginData(cloneDeep(oldLayer.current));
                                editor.font.register(editor.stage.getLayer(id), fontFamily, false);
                            }}
                            onBlur={() => {
                                if (oldLayer.current && originValue.current && originValue.current.text !== text) {
                                    editor.markupLayer.setLabelType(id, type, text, num, units, oldLayer.current);
                                    onAfterChange?.(trackCode?.text);
                                }
                                editor.markupLayer.clearAttrOperateOriginData();
                            }}
                        />
                    </div>
                </LabelAction>
            ) : null}
        </div>
    );
});
